import { Fragment, useState } from "react";

import { euro, type WebInvoiceLine } from "../../lib/invoices";
import { gruppiereZeilen } from "../../lib/invoiceBlocks";
import { ablageErlaubt, gliederung, verschiebeZu } from "../../lib/invoiceUmordnen";

/**
 * Der Index des Belegs — **der** Weg zum Umsortieren.
 *
 * Die Zeilenpfeile sind dafür abgeschafft (Benjamin, 01.09.2026): eine
 * Position über zehn Stellen zu bewegen waren zehn Klicks, und ein Block ließ
 * sich nur als Ganzes an seinem Nachbarn vorbeischieben. Hier steht der Beleg
 * auf kleinem Raum: eine Zeile je Position, Blöcke als Überschrift mit ihren
 * Positionen darunter — kurz genug, dass Anfang und Ende gleichzeitig sichtbar
 * sind, und genau das braucht man beim Umsortieren.
 *
 * Er steht **über** der Positionsliste. Darunter lag er bei mehr als drei
 * Positionen außerhalb des Bildschirms, und der Knopf wirkte kaputt.
 */

export type GliederungProps = {
  lines: WebInvoiceLine[];
  onChange: (lines: WebInvoiceLine[]) => void;
  onSchliessen: () => void;
  /** Dieselbe Menge wie in der Gesamtansicht — ein Block ist an beiden Stellen
      gleichzeitig auf oder zu (Benjamin, 01.09.2026). */
  eingeklappt: Set<string>;
  onToggleEingeklappt: (id: string) => void;
};

export default function InvoiceGliederung({
  lines, onChange, onSchliessen, eingeklappt, onToggleEingeklappt,
}: GliederungProps) {
  /** Index der gezogenen Zeile; -1 = nichts unterwegs. */
  const [zieht, setZieht] = useState(-1);
  /** Stelle, VOR die abgelegt würde. */
  const [ziel, setZiel] = useState(-1);

  const zeilen = gliederung(lines);
  const netto = (line: WebInvoiceLine) => (
    (Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0)
  );
  const blockSumme = (blockIndex: number, kind: WebInvoiceLine["line_kind"]) => {
    const gruppe = gruppiereZeilen(lines).find((g) => g.blockIndex === blockIndex);
    return (gruppe?.zeilen || []).reduce((s, { line }) => (
      line.line_kind === kind ? s + netto(line) : s
    ), 0);
  };
  const blockNetto = (blockIndex: number) => blockSumme(blockIndex, "position");
  /** Wie im Positionseditor und im PDF: die Optionalsumme steht daneben,
      nie darin. Ohne sie widerspräche die Kurzfassung der Gesamtansicht. */
  const blockOptionalNetto = (blockIndex: number) => blockSumme(blockIndex, "optional");

  const ablegen = (zielIndex: number) => {
    if (zieht >= 0 && ablageErlaubt(lines, zieht, zielIndex)) {
      onChange(verschiebeZu(lines, zieht, zielIndex));
    }
    setZieht(-1);
    setZiel(-1);
  };

  /** Die schmale Linie zwischen zwei Einträgen, auf der abgelegt wird. */
  const ablegeLinie = (zielIndex: number, schluessel: string) => (
    <li
      key={`ziel-${schluessel}`}
      className={"invoice-gl-ziel" + (ziel === zielIndex ? " is-aktiv" : "")}
      onDragOver={(event) => {
        if (zieht < 0 || !ablageErlaubt(lines, zieht, zielIndex)) return;
        event.preventDefault();
        setZiel(zielIndex);
      }}
      onDragLeave={() => setZiel((z) => (z === zielIndex ? -1 : z))}
      onDrop={(event) => { event.preventDefault(); ablegen(zielIndex); }}
      aria-hidden="true"
    />
  );

  return (
    <div
      className="invoice-gl-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onSchliessen(); }}
    >
      <aside className="invoice-gliederung" role="dialog" aria-modal="true" aria-label="Index">
      <header className="invoice-gl-kopf">
        <div>
          <p className="invoice-kicker">Index</p>
          <h3>Reihenfolge per Ziehen ändern</h3>
        </div>
        <button type="button" className="invoice-button" onClick={onSchliessen}>Schließen</button>
      </header>
      <p className="invoice-gl-hinweis">
        Zeile am Griff packen und zwischen zwei andere ziehen. Ein Block nimmt seine
        Positionen mit; eine Position lässt sich in einen anderen Block ziehen.
      </p>

      <ol className="invoice-gl-liste">
        {ablegeLinie(0, "start")}
        {zeilen.map((zeile) => {
          // Eine Position in einem eingeklappten Block tritt zurück — Kopf
          // und Zwischensumme genügen für die Übersicht, die einzelnen
          // Zeilen muss man dafür nicht sehen.
          if (!zeile.istBlock && zeile.blockId && eingeklappt.has(zeile.blockId)) return null;
          const zu = zeile.istBlock && eingeklappt.has(zeile.line.id);
          return (
            <Fragment key={zeile.line.id}>
              <li
                className={"invoice-gl-zeile"
                  + (zeile.istBlock ? " is-block" : "")
                  + (zeile.imBlock ? " is-eingerueckt" : "")
                  + (zieht === zeile.index ? " is-zieht" : "")}
                draggable
                onDragStart={(event) => {
                  setZieht(zeile.index);
                  event.dataTransfer.effectAllowed = "move";
                  // Firefox startet ohne gesetzte Daten gar keinen Zug.
                  event.dataTransfer.setData("text/plain", zeile.line.id);
                }}
                onDragEnd={() => { setZieht(-1); setZiel(-1); }}
                // Ein Klick auf die Blockzeile klappt sie — wie in der
                // Gesamtansicht, nur ohne Bearbeiten (Benjamin, 02.09.2026).
                onClick={zeile.istBlock ? () => onToggleEingeklappt(zeile.line.id) : undefined}
                title={zeile.istBlock ? (zu ? "Block aufklappen" : "Block einklappen") : undefined}
              >
                <span className="invoice-gl-griff" aria-hidden="true">⠿</span>
                {zeile.istBlock && (
                  <span className="invoice-block-chevron" data-eingeklappt={zu ? "true" : "false"} aria-hidden="true">▾</span>
                )}
                <span className="invoice-gl-text">
                  <strong>{zeile.line.title.trim() || (zeile.istBlock ? "Block ohne Überschrift" : "Ohne Bezeichnung")}</strong>
                  {!zeile.istBlock && (
                    <small>
                      {zeile.line.quantity} {zeile.line.unit_label}
                      {zeile.line.line_kind === "optional" && " · optional"}
                    </small>
                  )}
                </span>
                <span className="invoice-gl-netto">
                  {zeile.istBlock ? euro(blockNetto(zeile.index)) : euro(netto(zeile.line))}
                  {zeile.istBlock && blockOptionalNetto(zeile.index) > 0 && (
                    <small>+ {euro(blockOptionalNetto(zeile.index))} optional</small>
                  )}
                </span>
              </li>
              {ablegeLinie(zeile.index + 1, zeile.line.id)}
            </Fragment>
          );
        })}
      </ol>
      </aside>
    </div>
  );
}
